import { supabase } from '@/integrations/supabase/client';
import { STORAGE_KEYS, STORAGE_KEY_TO_TABLE_MAP } from '../constants/storage-keys';
import { saveLocalData, loadData } from '../utils/local-storage';
import { entriesCache } from '../utils/cache-utils';
import { getUserSettings } from './data-sync-service';
import { toast } from 'sonner';
import { eventBus, SYNC_EVENTS } from '../utils';
import { format, subDays, parseISO, isAfter } from 'date-fns';
import { formatDateToISO } from '../date-utils';

// Add mapping functions for ActiveFastingSession
interface ActiveFastingSession {
  id: string;
  user_id: string;
  startTime: string;
  endTime: string | null;
  duration: number | null;
  timestamp: string;
  type: 'fasting';
}

const mapActiveFastingToDB = (session: ActiveFastingSession | null) => {
  if (!session) return null;
  return {
    id: session.id,
    user_id: session.user_id,
    start_time: session.startTime,
    end_time: session.endTime,
    duration: session.duration,
    timestamp: session.timestamp,
  };
};

const mapActiveFastingFromDB = (dbSession: any) => {
  if (!dbSession) return null;
  return {
    id: dbSession.id,
    user_id: dbSession.user_id,
    startTime: dbSession.start_time,
    endTime: dbSession.end_time,
    duration: dbSession.duration,
    timestamp: dbSession.timestamp,
    type: 'fasting',
  } as ActiveFastingSession;
};

const getLastSyncTimestamp = (key: string): string | null => {
  try {
    const syncInfo = localStorage.getItem(`sync_info_${key}`);
    if (!syncInfo) return null;
    const parsed = JSON.parse(syncInfo);
    return parsed.lastSync || null;
  } catch (e) {
    console.error('Error parsing last sync timestamp:', e);
    return null;
  }
};

const saveLastSyncTimestamp = (key: string, timestamp: string): void => {
  try {
    localStorage.setItem(`sync_info_${key}`, JSON.stringify({
      lastSync: timestamp
    }));
  } catch (e) {
    console.error('Error saving last sync timestamp:', e);
  }
};

const loadActiveFasting = async () => {
  try {
    const { data: sessionData } = await supabase.auth.getSession();
    const userId = sessionData.session?.user?.id;
    
    if (!userId) {
      if (import.meta.env.DEV) console.log('User not authenticated, skipping active fasting load');
      return null;
    }
    
    const { data, error } = await supabase
      .from('active_fasting')
      .select('*')
      .eq('user_id', userId)
      .maybeSingle();
      
    if (error) {
      console.error('Error loading active fasting session:', error);
      throw error;
    }
    
    if (!data) return null;
    
    const activeFastingSession = mapActiveFastingFromDB(data);
    if (import.meta.env.DEV) console.log('Loaded active fasting session:', activeFastingSession);
    
    return activeFastingSession;
  } catch (error) {
    console.error('Error in loadActiveFasting:', error);
    return null;
  }
};

type TableName = 'food_entries' | 'walking_entries' | 'journal_entries' | 'health_entries' | 'water_entries' | 'fasting_sessions' | 'active_fasting' | 'favorite_foods' | 'user_settings';

export const loadUserData = async (key: string, defaultValue: any, dateFilter: string[] = [], lastSync: string | null = null) => {
  try {
    const { data: sessionData } = await supabase.auth.getSession();
    const userId = sessionData.session?.user?.id;
    
    if (!userId) {
      if (import.meta.env.DEV) console.log('User not authenticated, skipping data load');
      return defaultValue;
    }
    
    if (key === STORAGE_KEYS.USER_SETTINGS) {
      return getUserSettings(userId, defaultValue);
    }
    
    const tableName = STORAGE_KEY_TO_TABLE_MAP[key] as TableName | undefined;
    if (!tableName) {
      console.error(`No table mapping found for key: ${key}`);
      return defaultValue;
    }
    
    let data: any[] | null = null;
    let error: any = null;
    
    switch (tableName) {
      case 'food_entries': {
        let query = supabase.from('food_entries').select('*').eq('user_id', userId);
        if (dateFilter.length > 0) query = query.in('date', dateFilter);
        if (lastSync) query = query.gt('timestamp', lastSync);
        const result = await query;
        data = result.data;
        error = result.error;
        break;
      }
      case 'walking_entries': {
        let query = supabase.from('walking_entries').select('*').eq('user_id', userId);
        if (dateFilter.length > 0) query = query.in('date', dateFilter);
        if (lastSync) query = query.gt('timestamp', lastSync);
        const result = await query;
        data = result.data;
        error = result.error;
        break;
      }
      case 'journal_entries': {
        let query = supabase.from('journal_entries').select('*').eq('user_id', userId);
        if (dateFilter.length > 0) query = query.in('date', dateFilter);
        if (lastSync) query = query.gt('timestamp', lastSync);
        const result = await query;
        data = result.data;
        error = result.error;
        break;
      }
      case 'health_entries': {
        let query = supabase.from('health_entries').select('*').eq('user_id', userId);
        if (dateFilter.length > 0) query = query.in('date', dateFilter);
        if (lastSync) query = query.gt('timestamp', lastSync);
        const result = await query;
        data = result.data;
        error = result.error;
        break;
      }
      case 'water_entries': {
        let query = supabase.from('water_entries').select('*').eq('user_id', userId);
        if (dateFilter.length > 0) query = query.in('date', dateFilter);
        if (lastSync) query = query.gt('timestamp', lastSync);
        const result = await query;
        data = result.data;
        error = result.error;
        break;
      }
      case 'fasting_sessions': {
        let query = supabase.from('fasting_sessions').select('*').eq('user_id', userId);
        if (dateFilter.length > 0) query = query.in('date', dateFilter);
        if (lastSync) query = query.gt('timestamp', lastSync);
        const result = await query;
        data = result.data;
        error = result.error;
        break;
      }
      case 'active_fasting': {
        const result = await supabase.from('active_fasting').select('*').eq('user_id', userId);
        data = result.data;
        error = result.error;
        break;
      }
      case 'favorite_foods': {
        const result = await supabase.from('favorite_foods').select('*').eq('user_id', userId);
        data = result.data;
        error = result.error;
        break;
      }
      case 'user_settings': {
        const result = await supabase.from('user_settings').select('*').eq('user_id', userId);
        data = result.data;
        error = result.error;
        break;
      }
      default:
        console.error(`Unknown table name: ${tableName}`);
        return defaultValue;
    }
    
    if (error) {
      console.error(`Error loading data for ${key}:`, error);
      throw error;
    }
    
    return data || defaultValue;
  } catch (error) {
    console.error(`Error loading data for ${key}:`, error);
    return defaultValue;
  }
};

export const syncAllData = async (): Promise<void> => {
  try {
    if (import.meta.env.DEV) console.log('Starting optimized data sync...');
    
    const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
    
    if (sessionError) {
      console.error('Session error in syncAllData:', sessionError);
      throw new Error(`Authentication error: ${sessionError.message}`);
    }
    
    const userId = sessionData.session?.user?.id;
    
    if (!userId) {
      if (import.meta.env.DEV) console.log('User not authenticated in syncAllData, skipping sync');
      return;
    }

    const today = new Date();
    const dates = [
      formatDateToISO(today),
      formatDateToISO(subDays(today, 1)),
      formatDateToISO(subDays(today, 2)),
    ];
    
    if (import.meta.env.DEV) console.log('Syncing data for dates:', dates);
    
    if (import.meta.env.DEV) console.log('Clearing specific entries cache');
    for (const date of dates) {
      const cacheKey = `entries_${date}`;
      if (entriesCache[cacheKey]) {
        delete entriesCache[cacheKey];
      }
    }

    const syncPriorities = [
      STORAGE_KEYS.FOOD_ENTRIES,
      STORAGE_KEYS.WATER_ENTRIES,
      STORAGE_KEYS.FASTING_SESSIONS,
      STORAGE_KEYS.ACTIVE_FASTING,
      STORAGE_KEYS.HEALTH_ENTRIES,
      STORAGE_KEYS.WALK_ENTRIES,
      STORAGE_KEYS.JOURNAL_ENTRIES
    ];
    
    const keys = [...syncPriorities];
    
    const syncWithRetry = async (key: string, defaultValue: any, retries = 2, dateFilter = dates): Promise<any> => {
      const lastSync = getLastSyncTimestamp(key);
      if (import.meta.env.DEV) console.log(`Last sync for ${key}: ${lastSync || 'never'}`);
      
      for (let i = 0; i < retries + 1; i++) {
        try {
          const data = await loadUserData(key, defaultValue, dateFilter, lastSync);
          if (import.meta.env.DEV) console.log(`Synced ${key}:`, Array.isArray(data) ? `${data.length} items` : 'single item');
          
          saveLastSyncTimestamp(key, new Date().toISOString());
          
          return data;
        } catch (err) {
          console.error(`Error syncing ${key} (attempt ${i + 1}):`, err);
          if (i === retries) throw err;
          
          const delay = 1000 * Math.pow(2, i);
          if (import.meta.env.DEV) console.log(`Retrying in ${delay}ms...`);
          await new Promise(resolve => setTimeout(resolve, delay));
        }
      }
      
      return defaultValue;
    };
    
    let successCount = 0;
    let failureCount = 0;
    
    for (const key of keys) {
      try {
        const syncPromise = (async () => {
          try {
            if (key === STORAGE_KEYS.ACTIVE_FASTING) {
              const activeFastingData = await loadActiveFasting();
              if (activeFastingData) {
                saveLocalData(STORAGE_KEYS.ACTIVE_FASTING, activeFastingData);
                if (import.meta.env.DEV) console.log('Successfully synced active fasting session');
              }
              return;
            }
            
            const existingData = loadData(key, []);
            const newData = await syncWithRetry(key, []);
            
            if (Array.isArray(newData) && Array.isArray(existingData)) {
              const existingMap = new Map(existingData.map((item: any) => [item.id, item]));
              
              newData.forEach((item: any) => {
                const existingItem = existingMap.get(item.id) as any;
                if (existingItem && 
                    item.timestamp && 
                    existingItem.timestamp && 
                    isAfter(parseISO(item.timestamp), parseISO(existingItem.timestamp))) {
                  existingMap.set(item.id, item);
                } 
                else if (!existingItem) {
                  existingMap.set(item.id, item);
                }
              });
              
              const mergedData = Array.from(existingMap.values());
              saveLocalData(key, mergedData);
              if (import.meta.env.DEV) console.log(`Successfully merged and saved ${mergedData.length} items for ${key}`);
            }
          } catch (error) {
            throw error;
          }
        })();
        
        const syncTimeout = new Promise((_, reject) => {
          setTimeout(() => reject(new Error(`Sync for ${key} timed out`)), 10000);
        });
        
        await Promise.race([syncPromise, syncTimeout]);
        
        successCount++;
        
        await new Promise(resolve => setTimeout(resolve, 100));
      } catch (error) {
        console.error(`Failed to sync ${key}:`, error);
        failureCount++;
        continue;
      }
    }
    
    if (import.meta.env.DEV) console.log(`Data sync completed: ${successCount} successful, ${failureCount} failed`);
    
    eventBus.dispatch(SYNC_EVENTS.ALL_DATA_UPDATED);
    
    if (successCount > 0 && failureCount > 0) {
      if (import.meta.env.DEV) console.warn(`Partial sync success: ${failureCount} sync operations failed`);
    }
    else if (successCount === 0 && failureCount > 0) {
      throw new Error(`All sync operations failed`);
    }
    else if (successCount > 0 && failureCount === 0) {
      if (import.meta.env.DEV) console.log('All data sync operations completed successfully');
    }
  } catch (error) {
    console.error('Error in syncAllData:', error);
    throw error;
  }
};

if (SYNC_EVENTS) {
  SYNC_EVENTS.SYNC_COMPLETED = SYNC_EVENTS.SYNC_COMPLETED || 'sync_completed';
  SYNC_EVENTS.SYNC_ERROR = SYNC_EVENTS.SYNC_ERROR || 'sync_error';
  SYNC_EVENTS.LOCAL_UPDATE = SYNC_EVENTS.LOCAL_UPDATE || 'local_update';
}
